import type {
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributeAnchorTarget,
  HTMLAttributeReferrerPolicy,
} from "react";
import React from "react";
import clsx from "clsx";
import type { IBorderable, IColorable, ISizable } from "../types";
import { cssUnit, getSizes } from "../types";
import styleModule from "./button.module.scss";
import { getColor, getRadius } from "../../scss";

const sizes = ["small", "medium", "large"] as const;

const variants = ["outlined", "text", "icon", "flat"] as const;

type TSizesProperty = (typeof sizes)[number];
type TvariantsProperty = (typeof variants)[number];
export interface ButtonProps
  extends ISizable,
    IBorderable,
    Pick<IColorable, "bgColor">,
    ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode | React.ReactNode[];
  style?: CSSProperties;
  loading?: boolean;
  href?: string;
  iconName?: string;
  size?: TSizesProperty;
  variant?: TvariantsProperty;
  referrerPolicy?: HTMLAttributeReferrerPolicy | undefined;
  rel?: string | undefined;
  target?: HTMLAttributeAnchorTarget | undefined;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref) => {
    const {
      name,
      type = "button",
      value,
      bgColor = "neutral-25",
      color = "neutral-400",
      borderColor = "neutral",
      borderRadius = "1",
      borderThickness,
      borderType = "solid",
      children,
      loading,
      href,
      height = "100%",
      width = "100%",
      iconName,
      size = "medium",
      variant = "flat",
      disabled,
      ...restProps
    } = props;

    const styles = {
      backgroundColor: ["outlined", "flat"].includes(variant)
        ? getColor(bgColor)
        : undefined,
      width: cssUnit(width),
      height: cssUnit(height),
      color: color,
      borderRadius: getRadius(borderRadius),
      border:
        borderThickness &&
        `${cssUnit(borderThickness)} ${borderType} ${
          variant === "outlined"
            ? getColor(bgColor) || getColor(borderColor)
            : getColor(borderColor)
        }`,
      ...getSizes(props),
      ...props.style,
    };

    const getSizesStyle = (selectedSize: TSizesProperty) => {
      return sizes.map((sizeItem: TSizesProperty) => {
        sizeItem === selectedSize && styleModule[`button__${selectedSize}`];
      });
    };
    return React.createElement(
      href ? "a" : "button",
      {
        ...restProps,
        ref: ref,
        disabled: loading || props.disabled,
        name: name,
        type: type,
        value: value,
        className: clsx(
          props.className,
          styleModule["button"],
          (loading || props.disabled) && styleModule["button--disabled"],
          size && getSizesStyle(size),
        ),
        style: styles,
        href,
      },
      children,
    );
  },
);

Button.displayName = "Button";

export default Button;
